import { pt } from '../../utils/Point'
import { Direction } from '../../utils/SwipeHelper'
import Card from '../Card'

export const enum MonsterAbility {
  Persistant, // cannot discard the monster in the deck
  Greedy, // value of monster only increases on sliding
  Constant, // value of monster doesn't change by sliding
  Copycat, // copies whatever the highest value of nearby card is
  GoldDigger, // every time you pick up gold, the value of this monster goes up
  Infector, // infects one adjacent cards, making its loot convert to bugs
  Multiplier, // if there is an empty tile nearby, copies itself to that tile
  Shy, // puts itself at the bottom of the pile on appear
  PoisonNearby, // poisons nearby monsters. poison -> value reduces by 1 every turn/slide
  Alertable, // is alerted if an adjacent card is picked. alert -> cannot slide that row or column
  Booster, // on each action, randomly increases the power by 1 of an adjacent monster
}

export default class Monster extends Card {
  constructor(
    public name: string,
    public value: number,
    public loot?: Card,
    public abilities: MonsterAbility[] = []
  ) {
    super()
  }

  pick(picker = this.board!.hero.card) {
    if (!super.pick(picker)) return false

    const hero = this.board!.hero
    const movePickerHere = () => picker.moveTo(this.location!)

    if (picker === hero.card) {
      this.board!.hero.takeDamage(this.value)
      this.turnToLoot()
      this.loot?.pick()
      movePickerHere()
      this.drawCards()
      this.stack()
    } else {
      if (picker.value > this.value) {
        picker.takeDamage(this.value)
        this.turnToLoot()
        this.loot?.pick(picker)
        movePickerHere()
        this.drawCards()
        this.stack()
      } else if (picker.value < this.value) {
        this.takeDamage(picker.value)
        picker.turnToLoot()
        this.drawCards()
        picker.stack()
      } else {
        picker.takeDamage(picker.value)
        picker.turnToLoot()
        this.drawCards()
        picker.stack()

        this.takeDamage(this.value)
        this.turnToLoot()
        this.drawCards()
        this.stack()
      }
    }

    return true
  }

  isSpellbound() {
    return this.board?.hero.spellboundMonsters.has(this)
  }

  canSpellbind() {
    return this.board!.hero.selectedSpell
  }

  spellbind() {
    const hero = this.board!.hero
    const spell = this.board!.hero.selectedSpell!

    hero.spellboundMonsters.set(this, spell)

    this.board!.hero.consumeSelectedSpell()
  }

  heal(value: number) {
    this.value += value
  }

  takeDamage(value: number) {
    this.value -= value

    if (this.value <= 0) {
      this.value = 0

      if (this.isSpellbound())
        this.board!.hero.spellboundMonsters.delete(this)
      this.moveTo()
      this.drawCards()
    }
  }

  move(direction: Direction) {
    let delta = pt(0, 0)
    if (direction === Direction.Up) {
      delta = pt(0, -1)
    } else if (direction === Direction.Down) {
      delta = pt(0, 1)
    } else if (direction === Direction.Left) {
      delta = pt(-1, 0)
    } else if (direction === Direction.Right) {
      delta = pt(1, 0)
    }

    const card = this.board!.cardAt(this.location!.translate(delta))
    if (card) {
      card.pick(this)
    }
  }

  hasAbility(ability: MonsterAbility) {
    return this.abilities.includes(ability)
  }

  stack() {
    const currentDeck = this.board!.deckAt(this.location!.x, this.location!.y)!
    currentDeck.add(this.clone())
    this.riseStack()
  }

  turnToLoot() {
    if (this.loot)
      this.loot.moveTo(this.location, this.board)
    else this.moveTo()
  }

  clone() {
    return new Monster(this.name, this.value, this.loot, this.abilities)
  }
}
